import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@shadcn-in-nx/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@shadcn-in-nx/ui/tabs";
import { Button } from "@shadcn-in-nx/ui/button";
import { ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import StatusBadge from "./StatusBadge";
import ModalOverviewTab from "./ModalOverviewTab";
import ModalInitialDataTab from "./ModalInitialDataTab";
import ModalStepResultsTab from "./ModalStepResultsTab";
import ModalLogsTab from "./ModalLogsTab";
import ModalTechnicalTab from "./ModalTechnicalTab";

function ExecutionDetailModal({
    isOpen, onClose, execution, effectiveStatus,
    executionDetails, isLoadingDetails, formatDuration,
}) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [activeStep, setActiveStep] = React.useState(0);

    React.useEffect(() => { setActiveStep(0); }, [execution?.id]);

    const handleOpenWorkflow = (e) => {
        e.stopPropagation();
        if (executionDetails?.data?.initialData) {
            window.localStorage.setItem(`testData-${execution.workflowId}`, JSON.stringify(executionDetails.data.initialData));
        }
        window.localStorage.setItem(`executionId-${execution.workflowId}`, JSON.stringify(execution.id));
        onClose();
        navigate(`/chat/${execution.workflowId}/chat`);
    };

    if (!execution) return null;

    return (
        <Sheet open={isOpen} onOpenChange={onClose}>
            <SheetContent side="right" className="!w-[80vw] !max-w-none p-0 flex flex-col">
                <SheetHeader className="pl-4 pr-12 pt-4 pb-3 border-b space-y-2">
                    <div className="flex items-center justify-between">
                        <SheetTitle className="text-sm font-semibold truncate pr-2">{execution.workflowName}</SheetTitle>
                        <div className="flex items-center gap-2 shrink-0">
                            <StatusBadge status={effectiveStatus} />
                            <Button variant="outline" size="sm" onClick={handleOpenWorkflow} className="text-xs gap-1 h-7">
                                <ExternalLink className="h-3 w-3" />
                                {t("monitoring.card.openWorkflow")}
                            </Button>
                        </div>
                    </div>
                </SheetHeader>
                <div className="flex-1 overflow-hidden">
                    <Tabs defaultValue="overview" className="h-full flex flex-col">
                        <TabsList className="grid w-full grid-cols-5 rounded-none border-b bg-transparent h-9 px-4">
                            <TabsTrigger value="overview" className="text-[11px] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none">{t("monitoring.modal.tabOverview")}</TabsTrigger>
                            <TabsTrigger value="initial" className="text-[11px] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none">{t("monitoring.modal.tabInitialData")}</TabsTrigger>
                            <TabsTrigger value="steps" className="text-[11px] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none">{t("monitoring.modal.tabStepResults")}</TabsTrigger>
                            <TabsTrigger value="logs" className="text-[11px] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none">{t("monitoring.modal.tabLogs")}</TabsTrigger>
                            <TabsTrigger value="details" className="text-[11px] data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-foreground rounded-none">{t("monitoring.modal.tabTechnical")}</TabsTrigger>
                        </TabsList>
                        <div className="flex-1 overflow-hidden">
                            <TabsContent value="overview" className="h-full m-0 p-4">
                                <ModalOverviewTab execution={execution} effectiveStatus={effectiveStatus} formatDuration={formatDuration} />
                            </TabsContent>
                            <TabsContent value="initial" className="h-full m-0">
                                <ModalInitialDataTab isLoading={isLoadingDetails} executionDetails={executionDetails} />
                            </TabsContent>
                            <TabsContent value="steps" className="h-full m-0">
                                <ModalStepResultsTab isLoading={isLoadingDetails} executionDetails={executionDetails} activeStep={activeStep} onStepChange={setActiveStep} />
                            </TabsContent>
                            <TabsContent value="logs" className="h-full m-0">
                                <ModalLogsTab isLoading={isLoadingDetails} executionDetails={executionDetails} activeStep={activeStep} onStepChange={setActiveStep} />
                            </TabsContent>
                            <TabsContent value="details" className="h-full m-0 p-4">
                                <ModalTechnicalTab execution={execution} executionDetails={executionDetails} />
                            </TabsContent>
                        </div>
                    </Tabs>
                </div>
            </SheetContent>
        </Sheet>
    );
}

export default ExecutionDetailModal;
